/* ============================================================================
   SoFlo GTA Map  —  window.SFGTA
   One shared, Vice-City / GTA-styled Mapbox GL map used everywhere the store
   used to show a "radar": the home delivery-zone check, the member dashboard
   delivery zone, and the live order-tracking page.

   The Mapbox token here is a PUBLIC token (pk.*) and is meant to ship in the
   browser — restrict it by URL in the Mapbox dashboard for production.

   Usage:
     var m = SFGTA.create(el, { center:[lng,lat], zoom:9 });
     m.hub(lng, lat);                  // pink HQ blip
     m.zone(lng, lat, 50);             // neon delivery-radius ring
     m.you(lng, lat, true);            // cyan "you" blip (inZone => cyan/green)
     m.fit([[lng,lat],[lng,lat]]);     // frame a set of points
     m.marker('driver','driver',lng,lat,'DRIVER'); m.move('driver',lng,lat);
     m.trail([[lng,lat], ...]);        // driver breadcrumb
   ========================================================================== */
(function () {
  if (window.SFGTA) return;

  /* Mapbox public token. Public by design (it ships in this file), so the protection is not
     secrecy - it is the URL restriction on the token itself, set in the Mapbox console. This one
     is scoped to soflosmokeclub.com: verified 200 from our own pages, 403 from any other referer
     and 403 with no referer at all. The token it replaced was unrestricted, so anyone could lift
     it from this file and bill map loads to our account.
     Two things will silently break the map if changed: sending no Referer (do NOT add
     Referrer-Policy: no-referrer or <meta name="referrer"> to the theme), and swapping in a token
     that has no URL restriction - which fails open and costs money rather than erroring. */
  var TOKEN = 'pk.eyJ1IjoiZHJhZ2ljb3QiLCJhIjoiY210ZGh1Mm1tMGljOTJ6b2ZzeWdkcHB0YSJ9.NX4z-Xvwu7CCSqsHCVHvCQ';

  /* HUD + marker styles are injected here (not loaded as a .css asset) so styling
     never depends on Shopify's freshly-uploaded-asset MIME type, which can be
     served empty and get refused under strict MIME checking. */
  var CSS = '' +
  '.sfg-map{position:relative;overflow:hidden;isolation:isolate}' +
  '.sfg-map [data-gmap]{position:absolute;inset:0;width:100%;height:100%}' +
  '.sfg-map .mapboxgl-canvas{outline:none}' +
  '.sfg-map .mapboxgl-ctrl-bottom-right{z-index:4}' +
  '.sfg-map .mapboxgl-ctrl-attrib{background:rgba(6,8,18,.55)}' +
  '.sfg-map .mapboxgl-ctrl-attrib a{color:#8ea0c8}' +
  '.sfg-map.sfg-failed [data-gmap]{background:radial-gradient(circle at 50% 45%,rgba(255,45,120,.18),transparent 60%),repeating-linear-gradient(0deg,rgba(0,229,255,.06) 0 1px,transparent 1px 22px),repeating-linear-gradient(90deg,rgba(0,229,255,.06) 0 1px,transparent 1px 22px),#070b18}' +
  '.sfg-hud{position:absolute;inset:0;pointer-events:none;z-index:3;border-radius:inherit}' +
  '.sfg-hud__scan{position:absolute;inset:0;border-radius:inherit;background:repeating-linear-gradient(0deg,rgba(0,0,0,.16) 0 1px,transparent 1px 3px);mix-blend-mode:overlay;opacity:.5}' +
  '.sfg-hud__vig{position:absolute;inset:0;border-radius:inherit;box-shadow:inset 0 0 70px 14px rgba(0,0,0,.6),inset 0 0 0 1px rgba(0,229,255,.16)}' +
  '.sfg-hud__frame{position:absolute;inset:0;border-radius:inherit;box-shadow:0 0 0 1px rgba(255,45,120,.45),inset 0 0 26px rgba(255,45,120,.1)}' +
  '.sfg-hud__tag{position:absolute;left:14px;top:14px;z-index:4;pointer-events:none;font:800 .64rem/1 "Rajdhani",sans-serif;letter-spacing:.18em;text-transform:uppercase;color:#fff;text-shadow:0 0 8px rgba(255,45,120,.9),0 1px 2px #000}' +
  '.sfg-hud__tag b{color:#ff2d78}' +
  '.sfg-blip{position:relative;display:flex;align-items:center;justify-content:center;will-change:transform}' +
  '.sfg-blip__dot{display:flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:50%;line-height:1;font-size:15px;background:#ff2d78;border:2px solid #fff;box-shadow:0 3px 9px rgba(0,0,0,.5)}' +
  '.sfg-blip__ring{position:absolute;width:30px;height:30px;border-radius:50%;border:2px solid currentColor;opacity:0}' +
  '.sfg-blip__lbl{position:absolute;top:-19px;left:50%;transform:translateX(-50%);font:800 9px/1 "Rajdhani",sans-serif;letter-spacing:.14em;white-space:nowrap;color:#eafcff;text-shadow:0 0 4px #000,0 1px 2px #000}' +
  '.sfg-blip--hub{color:rgba(255,45,120,.6)}' +
  '.sfg-blip--hub .sfg-blip__dot{background:#ff2d78;box-shadow:0 0 16px rgba(255,45,120,.85),0 3px 9px rgba(0,0,0,.5)}' +
  '.sfg-blip--hub .sfg-blip__dot::after{content:"";width:9px;height:9px;border-radius:50%;background:#fff;box-shadow:0 0 7px #fff}' +
  '.sfg-blip--hub .sfg-blip__lbl{color:#ffd6e6}' +
  '.sfg-blip--you{color:rgba(0,229,255,.65)}' +
  '.sfg-blip--you .sfg-blip__dot{width:20px;height:20px;background:#00e5ff;box-shadow:0 0 16px rgba(0,229,255,.9)}' +
  '.sfg-blip--you .sfg-blip__ring{width:20px;height:20px;animation:sfgPulse 1.8s ease-out infinite}' +
  '.sfg-blip--you.is-out .sfg-blip__dot{background:#ffd84d;box-shadow:0 0 16px rgba(255,216,77,.85)}' +
  '.sfg-blip--you.is-out{color:rgba(255,216,77,.6)}' +
  '.sfg-blip--store .sfg-blip__dot{background:#fff;border-color:#ff2d78}' +
  '.sfg-blip--customer .sfg-blip__dot{background:#fff;border-color:#00e5ff}' +
  '.sfg-blip--driver{color:rgba(255,45,120,.6)}' +
  '.sfg-blip--driver .sfg-blip__dot{width:44px;height:44px;font-size:21px;background:#ff2d78;border:3px solid #fff;box-shadow:0 5px 16px rgba(255,45,120,.6)}' +
  '.sfg-blip--driver .sfg-blip__ring{width:44px;height:44px;animation:sfgPulse 1.8s ease-out infinite}' +
  '@keyframes sfgPulse{0%{transform:scale(.6);opacity:.85}100%{transform:scale(1.7);opacity:0}}' +
  '@media (prefers-reduced-motion:reduce){.sfg-blip__ring{animation:none!important}}';
  (function injectCSS(){
    if (document.getElementById('sfg-style')) return;
    var st = document.createElement('style'); st.id = 'sfg-style'; st.textContent = CSS;
    (document.head || document.documentElement).appendChild(st);
  })();

  var GL_VER = 'v3.9.0';
  var GL_JS  = 'https://api.mapbox.com/mapbox-gl-js/' + GL_VER + '/mapbox-gl.js';
  var GL_CSS = 'https://api.mapbox.com/mapbox-gl-js/' + GL_VER + '/mapbox-gl.css';
  // Pinned to v3.9.0. Bumping GL_VER means re-hashing both files.
  var GL_JS_SRI  = 'sha384-QRI8HEhaDamjVGfoos6X0AB+VVxCE4fAm6jaRZqcMn8mPsbxJvCc8cyG3Wu/+Pyq';
  var GL_CSS_SRI = 'sha384-GTsgKcJXGSkBp0M68qpxkz9XovzVH0PwSrjYONvkn3tXtySOSq+a14bG2gVJHwQG';

  /* ---- lazy-load the Mapbox GL library once, shared across every map ------- */
  var _loading = null;
  function loadGL() {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (_loading) return _loading;
    _loading = new Promise(function (res, rej) {
      if (!document.querySelector('link[data-sfg-glcss]')) {
        var lk = document.createElement('link');
        lk.rel = 'stylesheet'; lk.href = GL_CSS; lk.integrity = GL_CSS_SRI; lk.crossOrigin = 'anonymous'; lk.setAttribute('data-sfg-glcss', '');
        document.head.appendChild(lk);
      }
      var sc = document.createElement('script');
      sc.src = GL_JS; sc.async = true; sc.integrity = GL_JS_SRI; sc.crossOrigin = 'anonymous';
      sc.onload = function () { window.mapboxgl ? res(window.mapboxgl) : rej(new Error('mapboxgl missing')); };
      sc.onerror = function () { rej(new Error('mapbox-gl failed to load')); };
      document.head.appendChild(sc);
    });
    return _loading;
  }

  /* ---- Vice-City neon recolor of the stock dark-v11 style ------------------ */
  /* We start from mapbox/dark-v11 (a classic style with named layers) and walk
     every layer, recoloring water / land / roads / labels into the SoFlo neon
     palette. Robust to small id changes because it matches on keywords. */
  function neonify(map) {
    var WATER  = '#0e3a50';   // vice teal — kept clearly lighter than land so the coastline reads
    var LAND   = '#0a1326';   // midnight land
    var LAND2  = '#11203b';   // landuse / parks
    var BLD    = '#16264a';   // buildings
    var ST     = '#46578f';   // residential streets — bright enough to show the grid
    var SEC    = '#6f8ae0';   // secondary / tertiary
    var PRIM   = '#00e5ff';   // primary  -> cyan
    var HWY    = '#ff2d78';   // motorway -> hot pink
    var layers = (map.getStyle() && map.getStyle().layers) || [];
    layers.forEach(function (L) {
      var id = L.id, t = L.type;
      try {
        if (t === 'background') { map.setPaintProperty(id, 'background-color', LAND); return; }
        if (/water/.test(id)) {
          if (t === 'fill') { map.setPaintProperty(id, 'fill-color', WATER); map.setPaintProperty(id, 'fill-opacity', 1); }
          else if (t === 'line') { map.setPaintProperty(id, 'line-color', '#0d3a4d'); }
          return;
        }
        if (/(national-park|park|landcover|landuse|pitch|sand|grass)/.test(id) && t === 'fill') {
          map.setPaintProperty(id, 'fill-color', LAND2); map.setPaintProperty(id, 'fill-opacity', 0.55); return;
        }
        if (/building/.test(id)) {
          if (t === 'fill') { map.setPaintProperty(id, 'fill-color', BLD); map.setPaintProperty(id, 'fill-opacity', 0.65); }
          else if (t === 'fill-extrusion') { map.setPaintProperty(id, 'fill-extrusion-color', BLD); }
          return;
        }
        if (/(road|street|bridge|tunnel)/.test(id) && t === 'line') {
          var c = ST;
          if (/motorway|trunk/.test(id)) c = HWY;
          else if (/primary/.test(id)) c = PRIM;
          else if (/secondary|tertiary/.test(id)) c = SEC;
          map.setPaintProperty(id, 'line-color', c);
          if (/motorway|trunk|primary/.test(id)) { try { map.setPaintProperty(id, 'line-blur', 0.6); } catch (e) {} }
          return;
        }
        if (/(admin|boundary)/.test(id) && t === 'line') {
          map.setPaintProperty(id, 'line-color', '#2a3a66'); return;
        }
        if (t === 'symbol') {
          /* drop noisy road shields/labels; tint place + poi text into neon-white */
          if (/(shield|road-label|road-number)/.test(id)) { try { map.setLayoutProperty(id, 'visibility', 'none'); } catch (e) {} return; }
          try {
            map.setPaintProperty(id, 'text-color', /settlement|place|state|country/.test(id) ? '#dfe7ff' : '#9fb0e0');
            map.setPaintProperty(id, 'text-halo-color', '#04060f');
            map.setPaintProperty(id, 'text-halo-width', 1.5);
          } catch (e) {}
        }
      } catch (e) { /* never let one layer break the recolor */ }
    });
  }

  /* ---- geometry: a ring polygon for the delivery radius -------------------- */
  function ringPolygon(lng, lat, miles, steps) {
    steps = steps || 96;
    var km = miles * 1.609344;
    var dLat = km / 110.574;
    var dLng = km / (111.320 * Math.cos(lat * Math.PI / 180));
    var coords = [];
    for (var i = 0; i <= steps; i++) {
      var a = (i / steps) * 2 * Math.PI;
      coords.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
    }
    return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } };
  }

  var ICON = { hub: '', you: '', store: '🏪', customer: '🏠', driver: '🛵' };
  function blipEl(kind, label) {
    var w = document.createElement('div');
    w.className = 'sfg-blip sfg-blip--' + kind;
    var ico = ICON[kind] || '';
    w.innerHTML =
      '<span class="sfg-blip__ring"></span>' +
      '<span class="sfg-blip__dot">' + ico + '</span>' +
      (label ? '<span class="sfg-blip__lbl">' + label + '</span>' : '');
    return w;
  }

  /* ---- controller returned by create() ------------------------------------ */
  function makeController(opts) {
    var ctrl = { map: null, gl: null, _ready: false, _q: [], markers: {}, _anim: {} };

    ctrl.ready = function (fn) { ctrl._ready ? fn() : ctrl._q.push(fn); };

    ctrl.marker = function (id, kind, lng, lat, label) {
      ctrl.ready(function () {
        var m = ctrl.markers[id];
        if (m) { m.setLngLat([lng, lat]); }
        else {
          m = new ctrl.gl.Marker({ element: blipEl(kind, label), anchor: 'center' })
            .setLngLat([lng, lat]).addTo(ctrl.map);
          ctrl.markers[id] = m;
        }
      });
      return ctrl;
    };
    ctrl.remove = function (id) {
      ctrl.ready(function () { if (ctrl.markers[id]) { ctrl.markers[id].remove(); delete ctrl.markers[id]; } });
      return ctrl;
    };
    /* glide a marker to a new spot (polls are seconds apart -> reads as live) */
    ctrl.move = function (id, lng, lat) {
      ctrl.ready(function () {
        var m = ctrl.markers[id]; if (!m) { return; }
        var from = m.getLngLat(), i = 0, n = 26;
        if (ctrl._anim[id]) clearInterval(ctrl._anim[id]);
        ctrl._anim[id] = setInterval(function () {
          i++; var t = i / n;
          m.setLngLat([from.lng + (lng - from.lng) * t, from.lat + (lat - from.lat) * t]);
          if (i >= n) { clearInterval(ctrl._anim[id]); ctrl._anim[id] = null; }
        }, 50);
      });
      return ctrl;
    };

    ctrl.hub = function (lng, lat, label) { return ctrl.marker('hub', 'hub', lng, lat, label || 'SOFLO HQ'); };

    ctrl.you = function (lng, lat, inZone) {
      ctrl.ready(function () {
        var m = ctrl.markers.you;
        if (lng == null) { if (m) { m.remove(); delete ctrl.markers.you; } return; }
        if (!m) {
          m = new ctrl.gl.Marker({ element: blipEl('you', 'YOU'), anchor: 'center' })
            .setLngLat([lng, lat]).addTo(ctrl.map);
          ctrl.markers.you = m;
        } else { m.setLngLat([lng, lat]); }
        m.getElement().classList.toggle('is-out', inZone === false);
      });
      return ctrl;
    };

    /* neon delivery-radius ring: soft fill + glowing edge */
    ctrl.zone = function (lng, lat, miles) {
      ctrl.ready(function () {
        var data = ringPolygon(lng, lat, miles);
        if (ctrl.map.getSource('sfg-zone')) { ctrl.map.getSource('sfg-zone').setData(data); return; }
        ctrl.map.addSource('sfg-zone', { type: 'geojson', data: data });
        ctrl.map.addLayer({ id: 'sfg-zone-fill', type: 'fill', source: 'sfg-zone',
          paint: { 'fill-color': '#ff2d78', 'fill-opacity': 0.10 } });
        ctrl.map.addLayer({ id: 'sfg-zone-glow', type: 'line', source: 'sfg-zone',
          paint: { 'line-color': '#ff2d78', 'line-width': 7, 'line-blur': 6, 'line-opacity': 0.55 } });
        ctrl.map.addLayer({ id: 'sfg-zone-edge', type: 'line', source: 'sfg-zone',
          paint: { 'line-color': '#ff7db0', 'line-width': 2, 'line-dasharray': [3, 2], 'line-opacity': 0.95 } });
      });
      return ctrl;
    };

    /* upcoming route the driver will take: a road-following path from the driver
       to the destination (Mapbox Directions), drawn as a cyan "path ahead" so it
       reads distinctly from the pink past-breadcrumb trail. Falls back to a
       straight dashed line if Directions is unreachable. Throttled: only refetched
       when the driver has moved a meaningful distance (polls are seconds apart). */
    function metersBetween(a, b) {
      var dLat = (b[1] - a[1]) * 110574;
      var dLng = (b[0] - a[0]) * 111320 * Math.cos(a[1] * Math.PI / 180);
      return Math.sqrt(dLat * dLat + dLng * dLng);
    }
    ctrl._drawRoute = function (coords) {
      var data = { type: 'Feature', geometry: { type: 'LineString', coordinates: coords } };
      if (ctrl.map.getSource('sfg-route')) { ctrl.map.getSource('sfg-route').setData(data); return; }
      // keep the route beneath the past-trail layers if those exist
      var before = ctrl.map.getLayer('sfg-trail-case') ? 'sfg-trail-case' : undefined;
      ctrl.map.addSource('sfg-route', { type: 'geojson', data: data });
      ctrl.map.addLayer({ id: 'sfg-route-glow', type: 'line', source: 'sfg-route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#00e5ff', 'line-width': 9, 'line-blur': 7, 'line-opacity': 0.5 } }, before);
      ctrl.map.addLayer({ id: 'sfg-route-line', type: 'line', source: 'sfg-route',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#00e5ff', 'line-width': 4, 'line-dasharray': [1.4, 1.1], 'line-opacity': 0.95 } }, before);
    };
    ctrl.route = function (from, to) {
      ctrl.ready(function () {
        if (!from || !to) return;
        // skip a refetch unless the driver moved >35m or the destination changed
        var dest = to[0].toFixed(5) + ',' + to[1].toFixed(5);
        if (ctrl._routeFrom && ctrl._routeDest === dest && metersBetween(ctrl._routeFrom, from) < 35) return;
        ctrl._routeFrom = from; ctrl._routeDest = dest;
        var url = 'https://api.mapbox.com/directions/v5/mapbox/driving/' +
          from[0] + ',' + from[1] + ';' + to[0] + ',' + to[1] +
          '?geometries=geojson&overview=full&access_token=' + (opts.token || TOKEN);
        fetch(url).then(function (r) { return r.json(); }).then(function (j) {
          var rt = j && j.routes && j.routes[0];
          if (rt && rt.geometry && rt.geometry.coordinates && rt.geometry.coordinates.length > 1) {
            ctrl._drawRoute(rt.geometry.coordinates);
          } else { ctrl._drawRoute([from, to]); }
        }).catch(function () { ctrl._drawRoute([from, to]); });
      });
      return ctrl;
    };
    ctrl.clearRoute = function () {
      ctrl.ready(function () {
        ['sfg-route-line', 'sfg-route-glow'].forEach(function (id) { if (ctrl.map.getLayer(id)) ctrl.map.removeLayer(id); });
        if (ctrl.map.getSource('sfg-route')) ctrl.map.removeSource('sfg-route');
        ctrl._routeFrom = null; ctrl._routeDest = null;
      });
      return ctrl;
    };

    /* driver breadcrumb (white casing + pink core) */
    ctrl.trail = function (lngLats) {
      ctrl.ready(function () {
        if (!lngLats || lngLats.length < 2) return;
        var data = { type: 'Feature', geometry: { type: 'LineString', coordinates: lngLats } };
        if (ctrl.map.getSource('sfg-trail')) { ctrl.map.getSource('sfg-trail').setData(data); return; }
        ctrl.map.addSource('sfg-trail', { type: 'geojson', data: data });
        ctrl.map.addLayer({ id: 'sfg-trail-case', type: 'line', source: 'sfg-trail',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#ffffff', 'line-width': 8, 'line-opacity': 0.95 } });
        ctrl.map.addLayer({ id: 'sfg-trail-core', type: 'line', source: 'sfg-trail',
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#ff2d78', 'line-width': 4.5 } });
      });
      return ctrl;
    };

    ctrl.fit = function (lngLats, fitOpts) {
      ctrl.ready(function () {
        if (!lngLats || !lngLats.length) return;
        var b = new ctrl.gl.LngLatBounds(lngLats[0], lngLats[0]);
        lngLats.forEach(function (p) { b.extend(p); });
        ctrl.map.fitBounds(b, Object.assign({ padding: 60, maxZoom: 15, duration: 900 }, fitOpts || {}));
      });
      return ctrl;
    };
    ctrl.flyTo = function (lng, lat, zoom) {
      ctrl.ready(function () { ctrl.map.flyTo({ center: [lng, lat], zoom: zoom == null ? ctrl.map.getZoom() : zoom, duration: 1200 }); });
      return ctrl;
    };
    ctrl.setView = function (lng, lat, zoom) {
      ctrl.ready(function () { ctrl.map.jumpTo({ center: [lng, lat], zoom: zoom == null ? ctrl.map.getZoom() : zoom }); });
      return ctrl;
    };
    ctrl.on = function (ev, fn) { ctrl.ready(function () { ctrl.map.on(ev, fn); }); return ctrl; };

    return ctrl;
  }

  /* ---- public factory ----------------------------------------------------- */
  function create(el, opts) {
    opts = opts || {};
    var ctrl = makeController(opts);
    loadGL().then(function (mapboxgl) {
      mapboxgl.accessToken = opts.token || TOKEN;
      var map = new mapboxgl.Map({
        container: el,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: opts.center || [-80.2228, 25.7890],
        zoom: opts.zoom == null ? 9.1 : opts.zoom,
        minZoom: opts.minZoom || 3,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
        cooperativeGestures: !!opts.cooperativeGestures
      });
      ctrl.map = map; ctrl.gl = mapboxgl;
      try { map.touchZoomRotate.disableRotation(); } catch (e) {}
      map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
      if (opts.nav) map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('style.load', function () { neonify(map); });
      map.on('load', function () {
        ctrl._ready = true;
        ctrl._q.forEach(function (f) { try { f(); } catch (e) {} });
        ctrl._q = [];
        if (opts.onReady) opts.onReady(ctrl);
        // a beat later, mapbox sometimes needs a resize when mounted in a flex/grid cell
        setTimeout(function () { try { map.resize(); } catch (e) {} }, 60);
      });
      map.on('error', function (e) { /* keep going; map is decorative on radar surfaces */ });
    }).catch(function (err) {
      if (el && el.classList) el.classList.add('sfg-failed');
      if (opts.onError) opts.onError(err);
    });
    return ctrl;
  }

  window.SFGTA = { token: TOKEN, load: loadGL, create: create, ringPolygon: ringPolygon };
})();
